import styled from 'styled-components';
import { Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Flex,
  FlexColumn,
  OverflowTooltip,
  Radio,
} from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import type { Status } from 'incidents/types';
import { severityIconMap, statusIconMap } from 'incidents/utils';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from '../constants';

const Card = styled(Flex)<{ $selected: boolean }>`
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex: 0 0 auto;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.secondary.main : theme.palette.common.gray8};
  background: ${({ theme, $selected }) =>
    $selected
      ? alpha(theme.palette.secondary.main, 0.02)
      : theme.palette.common.white};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const CardRadio = styled(Radio)`
  padding: 0;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
`;

const SeverityIconWrapper = styled(Flex)`
  flex: 0 0 auto;
  width: 16px;
  justify-content: center;
`;

const IncidentTextContent = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

const titleSx = {
  fontSize: 14,
};

const titleTypographySx = {
  lineHeight: 1.4,
  fontWeight: '500 !important',
};

const SubLine = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const MetaLine = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const StatusIconWrapper = styled(Flex)`
  flex: 0 0 auto;
  width: 20px;
  justify-content: center;
`;

const PrimaryBadge = styled(Typography)`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.palette.common.blue7};
  text-transform: uppercase;
`;

export type MergeIncidentCardItem = {
  id: string;
  title: string;
  status: string;
  severity: string;
  startTime: string;
  lastFailureTime: string | null;
  monitors: {
    test: { name: string } | null;
    asset: { fqn: string; name: string } | null;
    signal: { lastStatus: string | null } | null;
  }[];
  events: {
    id: string;
  }[];
};

type Props = {
  incident: MergeIncidentCardItem;
  selected: boolean;
  onSelect: (id: string) => void;
};

const subtitleForIncident = (incident: MergeIncidentCardItem) => {
  const first = incident.monitors[0];
  const testName = first?.test?.name;
  const fqn = first?.asset?.fqn;
  if (testName && fqn) return `${testName} · ${fqn}`;
  return testName || fqn || '—';
};

const metaLine = (incident: MergeIncidentCardItem) => {
  const eventCount = incident.events.length;
  const first = localizeDate(incident.startTime).fromNow();
  const last = incident.lastFailureTime
    ? localizeDate(incident.lastFailureTime).fromNow()
    : '—';
  return `${pluralizeString(
    eventCount,
    'event',
    'events',
  )} · First ${first} · Last ${last}`;
};

const getSeverityIcon = (severity: string) => {
  const displaySeverity = SEVERITY_DISPLAY[severity.toLowerCase()] ?? severity;

  switch (displaySeverity) {
    case 'Low':
    case 'Normal':
    case 'High':
    case 'Critical':
      return severityIconMap[displaySeverity];
    default:
      return null;
  }
};

const getStatusIcon = (status: string) => {
  const displayStatus = STATUS_DISPLAY[status.toLowerCase()] ?? status;
  return statusIconMap[displayStatus as Status] ?? null;
};

const MergeIncidentCard = ({ incident, selected, onSelect }: Props) => (
  <Card $selected={selected} onClick={() => onSelect(incident.id)}>
    <CardRadio
      value={incident.id}
      checked={selected}
      onChange={() => onSelect(incident.id)}
    />
    <SeverityIconWrapper>
      {getSeverityIcon(incident.severity)}
    </SeverityIconWrapper>
    <IncidentTextContent>
      <OverflowTooltip sx={titleSx} typographySx={titleTypographySx}>
        {incident.title}
      </OverflowTooltip>
      <SubLine title={subtitleForIncident(incident)}>
        {subtitleForIncident(incident)}
      </SubLine>
      <MetaLine title={metaLine(incident)}>{metaLine(incident)}</MetaLine>
    </IncidentTextContent>
    <StatusIconWrapper>{getStatusIcon(incident.status)}</StatusIconWrapper>
    {selected && <PrimaryBadge>Primary</PrimaryBadge>}
  </Card>
);

export default MergeIncidentCard;
